<script lang="ts" setup>
import {
  computed,
  h,
  onMounted,
  ref,
  useRoute,
  useTemplateRef,
} from '#imports';
import type { TourProps } from 'ant-design-vue';
import { useService } from '@kaokei/use-vue-service';
import { useAdvancedDraggable } from '@core/composables/use-advanced-draggable';
import { StorageService } from '@core/services/storage.service';
import { ImService } from '../im.service';

const draggableTargetRef = useTemplateRef<HTMLDivElement>('draggable-target');
const storageService = useService(StorageService);
const service = useService(ImService);
const entryMsgText = '你有新的消息待查收';
const entryGuideVisible = ref(false);
const route = useRoute();

const tourSteps: TourProps['steps'] = [
  {
    title: h('div', [
      h('div', '现在做“课后练习”可以在线联系老师啦，'),
      h('div', '快来试试吧~'),
    ]),
    target: () => draggableTargetRef.value as HTMLElement,
    nextButtonProps: {
      children: () => '我知道了',
    },
    style: { width: '395px' },
  },
];

const { isDragging, dragged, style } = useAdvancedDraggable({
  disabled: entryGuideVisible,
  target: draggableTargetRef,
  container: 'draggable-container',
  bottom: 48,
  right: 48,
});

function closeEntryGuide() {
  entryGuideVisible.value = false;
}
function handleToggleEntry() {
  if (!dragged.value) {
    service.toggleEntry();
    closeEntryGuide();
  }
}

const entryText = computed(() =>
  service.unreadCount > 0 ? entryMsgText : service.peer.name,
);

const pageEvent = computed(() => ({
  page_name: route.name,
}));

onMounted(() => {
  // IM入口引导只展示一次
  const imEntryGuide = storageService.getImEntryGuide();
  if (!imEntryGuide) {
    entryGuideVisible.value = true;
    storageService.setImEntryGuide(1);
  }
});
</script>

<template>
  <div>
    <div
      ref="draggable-target"
      v-track:oj_show_im_icon="pageEvent"
      v-track:oj_click_im_icon.click="pageEvent"
      :style="style"
      class="im-entry-draggable-target border-colorFill fixed z-40 flex h-[60px] items-center justify-start rounded-full border bg-white pl-[3px] pr-5 shadow-[0px_0px_8px_0px_rgba(0,0,0,0.1)]"
      :class="isDragging ? 'cursor-grabbing' : 'cursor-grab'"
      @click="handleToggleEntry"
    >
      <a-badge
        dot
        :offset="[-6, 48]"
        class="im-status-badge"
        :class="{ online: service.peerOnline }"
      >
        <a-avatar
          :size="54"
          :draggable="false"
          :src="service.peer.avatar"
          class="select-none !border-none"
        />
      </a-badge>
      <div
        class="text-font1 ml-3 select-none whitespace-nowrap text-base font-bold"
      >
        {{ entryText }}
      </div>
      <a-badge
        v-show="service.unreadCount"
        class="im-entry-badge"
        :count="service.unreadCount"
      />
    </div>

    <a-tour
      type="primary"
      root-class-name="user-guide-tour pointer-enabled"
      :open="entryGuideVisible"
      :steps="tourSteps"
      :arrow="false"
      :popup-align="{ points: ['br', 'tr'], offset: [30, -54] }"
      :gap="{ offset: 30, radius: 4 }"
      @close="closeEntryGuide"
    />

    <Teleport to="body">
      <div
        ref="draggable-container"
        class="pointer-events-none fixed left-0 top-0 -z-10 h-full w-full"
      ></div>
    </Teleport>
  </div>
</template>

<style scoped>
.im-entry-draggable-target {
  border-width: 0.5px;
}

.im-entry-badge {
  @apply !ml-2;
}
.im-entry-badge :deep(.ant-badge-count) {
  @apply !h-4 !px-1 !leading-4;
}
.im-entry-badge :deep(.ant-scroll-number-only) {
  @apply !h-4 !leading-4;
}
.im-entry-badge :deep(.ant-scroll-number-only-unit) {
  @apply !h-4 !leading-4;
}

.im-status-badge :deep(.ant-badge-dot) {
  width: 9px;
  height: 9px;
  box-shadow: 0 0 0 1.5px #fff;
  @apply bg-tip;
}
.im-status-badge.online :deep(.ant-badge-dot) {
  @apply bg-success;
}
</style>
